import ForecastConfiguration from "./ForecastConfiguration.js";
import Model from "../../Model.js";
import ReportTemplateManager from "./ReportTemplateManager.js";
import ScenarioManager from "./ScenarioManager.js";

/**
 * A forecast template is a collection of scenarios and 3 report
 * templates (P&L, balance sheet and cash flow). A
 * `ForecastTemplateInterpreter` is responsible for converting a
 * `ForecastTemplate` into a `Forecast`.
 *
 * @param {string} id - Unique identifier of the forecast template
 * @param {Date} lastEditDate - Represent the last time the forecast
 *   was changed
 * @param {ScenarioManager} scenarioManager - This object is
 *   responsible for managing the collections of scenarios
 * @param {ReportTemplateManager} reportTemplateManager - This object
 *   is responsible for managing the 3 report templates
 * @param {ForecastConfiguration} configuration - This object
 *   maintains the configuration options of the template
 * @param {string} name - A user-visible short string to identify this
 *   forecast template
 * @param {boolean} isMain - A flag that identify the main forecast template
 */
export default class ForecastTemplate extends Model {
	constructor({
		id,
		lastEditDate,
		scenarioManager,
		reportTemplateManager,
		configuration = new ForecastConfiguration(),
		name,
		isMain,
	} = {}) {
		super(...arguments);

		this._id = id;
		this._lastEditDate = lastEditDate;
		this._scenarioManager = scenarioManager;
		this._reportTemplateManager = reportTemplateManager;
		this._configuration = configuration;
		this._name = name;
		this._isMain = isMain;
	}

	getId() {
		return this._id;
	}

	getLastEditDate() {
		return this._lastEditDate;
	}

	getConfiguration() {
		return this._configuration;
	}

	getChartOfAccountsId() {
		return this._configuration.getChartOfAccountsId();
	}

	getReportTemplateManager() {
		return this._reportTemplateManager;
	}

	getReportTemplates() {
		return this._reportTemplateManager.getReportTemplates();
	}

	getBaselineScenario() {
		return this._scenarioManager.getBaselineScenario();
	}

	getName() {
		return this._name;
	}

	getIsMain() {
		return this._isMain;
	}

	getSummaryValues() {
		return undefined;
	}

	getFrozenCurrentPeriod() {
		return this._configuration.getFrozenCurrentPeriod();
	}

	setFrozenCurrentPeriod(period) {
		this._configuration.setFrozenCurrentPeriod(period);
	}

	setName(name) {
		this._name = name;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.configuration = ForecastConfiguration.materialize(
			data.configuration,
			context,
		);

		data.reportTemplateManager = ReportTemplateManager.materialize(
			// report templates are not part of the server's
			// ForecastTemplate model so they have to come from the
			// context:
			context.reportTemplates,
			context,
		);

		data.scenarioManager = ScenarioManager.materialize(
			{
				scenarios: data.scenarios,
				baselineScenario: data.baselineScenario,
			},
			context,
		);

		data.lastEditDate = data.lastUpdated;
	}
}
